import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { dayFilterList, fetchTeamAppointmentHub, MenuProps, exportTeamAppointmentHub } from '../WidgetService';
import SchedulingSummary from '../../../Models/Widget/SchedulingSummary.model';
import { AppointmentTimeFrameEnum } from '../../../Enum/AppointmentTimeFrameEnum';
import { parentUrl } from '../../../utils/constants';
import { generateRandomId } from '../../../utils/common';
import TeamDropdown from '../../Shared/TeamDropdown';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import { fetchTeams } from '../../../store/careTeams.slice';
import '../Widgets.css';

const TeamAppointmentsHubDetail = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [members, setMembers] = useState<SchedulingSummary>();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const {
        schedulingDateFilter,
        setSchedulingDateFilter,
        schedulingFromDate,
        setSchedulingFromDate,
        schedulingToDate,
        setSchedulingToDate,
        schedulingSelectedPlans,
        setSchedulingSelectedPlans,
        schedulingSelectedProvider,
        setSchedulingSelectedProvider,
        schedulingSelectedStaff,
        setSchedulingSelectedStaff,
        schedulingSelectedTeam,
        setSchedulingSelectedTeam,
        schedulingShowInActiveProviders,
        setSchedulingShowInActiveProviders,
        schedulingShowInActiveTeams,
        setSchedulingShowInActiveTeams,
        schedulingShowInActiveStaffs,
        setSchedulingShowInActiveStaffs,
    } = useWidgetContext();
    const filteredProviders = (schedulingShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const filteredStaffs = (schedulingShowInActiveStaffs ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const hasElevatedFilterAccess = searchParams.get('hasElevatedFilterAccess') === 'true';

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterList = dayFilterList();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

    }, [isFetched, schedulingSelectedPlans]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (schedulingDateFilter !== AppointmentTimeFrameEnum.Custom || (Boolean(schedulingFromDate) && Boolean(schedulingToDate))) {
            const changeFilter = async () => {
                setIsMembersLoading(false);
                const data = await fetchTeamAppointmentHub(
                    schedulingDateFilter,
                    schedulingSelectedPlans.join(","),
                    schedulingSelectedProvider,
                    schedulingSelectedTeam,
                    schedulingSelectedStaff,
                    Boolean(schedulingFromDate) && schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? new Date(schedulingFromDate).toISOString() : '',
                    Boolean(schedulingToDate) && schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? new Date(schedulingToDate).toISOString() : '',
                    schedulingShowInActiveTeams,
                    schedulingShowInActiveProviders,
                    schedulingShowInActiveStaffs,
                    true
                );
                setMembers(data);
                setIsMembersLoading(true);
                setIsFetched(true);
            };
            changeFilter();
        }

        if (schedulingDateFilter !== AppointmentTimeFrameEnum.Custom && Boolean(schedulingFromDate) && Boolean(schedulingToDate)) {
            setSchedulingFromDate(null);
            setSchedulingToDate(null);
        }
    }, [
        schedulingDateFilter,
        schedulingSelectedPlans,
        schedulingSelectedProvider,
        schedulingSelectedStaff,
        schedulingFromDate,
        schedulingToDate,
        setSchedulingFromDate,
        setSchedulingToDate,
        setIsFetched,
        schedulingSelectedTeam,
        schedulingShowInActiveTeams,
        schedulingShowInActiveProviders,
        schedulingShowInActiveStaffs,
    ]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const changeFromDate = (date: any) => {
        setSchedulingFromDate(date);
    };

    const changeToDate = (date: any) => {
        setSchedulingToDate(date);
    };

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportTeamAppointmentHub(
            schedulingDateFilter,
            schedulingSelectedPlans.join(","),
            schedulingSelectedProvider,
            schedulingSelectedTeam,
            schedulingSelectedStaff,
            Boolean(schedulingFromDate) && schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? new Date(schedulingFromDate).toISOString() : '',
            Boolean(schedulingToDate) && schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? new Date(schedulingToDate).toISOString() : '',
            schedulingShowInActiveTeams,
            schedulingShowInActiveProviders,
            schedulingShowInActiveStaffs
        );
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TeamAppointmentHubSummary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    const toggleProviderHandler = (isChecked: boolean) => {
        setSchedulingShowInActiveProviders(isChecked);
        setSchedulingSelectedProvider('All');
    };

    const toggleStaffHandler = (isChecked: boolean) => {
        setSchedulingShowInActiveStaffs(isChecked);
        setSchedulingSelectedStaff('All');
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Appointment Date',
            width: 150,
            valueGetter: (value, row) => `${Boolean(row.DateInfo) ? row.DateInfo : '-'}`,
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Team Appointment Hub
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    {
                        hasElevatedFilterAccess && (<Grid size={1.4}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={schedulingSelectedTeam}
                                onChange={(id) => setSchedulingSelectedTeam(id)}
                                showInactive={schedulingShowInActiveTeams}
                                onToggleInactive={setSchedulingShowInActiveTeams}
                            />
                        </Grid>)
                    }
                    <Grid size={1.4}>
                        <HealthPlanMultiSelect
                            value={schedulingSelectedPlans}
                            onChange={setSchedulingSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={1.4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Providers</InputLabel>
                            <Select
                                value={schedulingSelectedProvider}
                                label="Providers"
                                name="Providers"
                                onChange={(e) => setSchedulingSelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={<Checkbox checked={schedulingShowInActiveProviders} onChange={(e) => toggleProviderHandler(e.target.checked)} />}
                                    label="Show Inactive"
                                />
                                {filteredProviders.map((pro) => (
                                    <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                        {pro.LastName}, {pro.FirstName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Scheduling Staff</InputLabel>
                            <Select
                                label="Scheduling Staff"
                                name="Scheduling Staff"
                                onChange={(e) => setSchedulingSelectedStaff(e.target.value)}
                                value={schedulingSelectedStaff}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={<Checkbox checked={schedulingShowInActiveStaffs} onChange={(e) => toggleStaffHandler(e.target.checked)} />}
                                    label="Show Inactive"
                                />
                                {filteredStaffs.map((pro) => (
                                    <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                        {pro.LastName}, {pro.FirstName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={schedulingDateFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setSchedulingDateFilter(parseInt(e.target.value))}
                            >
                                {filterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {parseInt(schedulingDateFilter) === AppointmentTimeFrameEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={1.3}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={Boolean(schedulingFromDate) ? schedulingFromDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={1.3}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(schedulingToDate) ? schedulingToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    {parseInt(schedulingDateFilter) !== AppointmentTimeFrameEnum.Custom ? <Grid size={3.8}></Grid> : <Grid size={1.2}></Grid>}
                    {
                        !hasElevatedFilterAccess && (
                            <Grid size={1.4}>
                            </Grid>)
                    }
                    <Grid size={1.2}>
                        <Button style={{ marginLeft: 'auto' }} onClick={() => exportDetail()} variant="contained" color="success">
                            {downLoading ? 'Exporting..' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2} paddingTop={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={4}>
                                <Grid size={4}>
                                    <Grid size={12} textAlign="center" paddingBottom={0.5}>
                                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                            Confirmed ({members.ConfirmedAppointmentMembers.length})
                                        </Typography>
                                    </Grid>
                                    <DataGrid
                                        sx={{ height: '60vh' }}
                                        columns={columns}
                                        rows={members.ConfirmedAppointmentMembers}
                                        getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Grid size={12} textAlign="center" paddingBottom={0.5}>
                                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                            Cancelled ({members.CancelledAppointmentMembers.length})
                                        </Typography>
                                    </Grid>
                                    <DataGrid
                                        sx={{ height: '60vh' }}
                                        columns={columns}
                                        rows={members.CancelledAppointmentMembers}
                                        getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Grid size={12} textAlign="center" paddingBottom={0.5}>
                                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                            No Call Or No Show ({members.NoCallOrNoShowAppointmentMembers.length})
                                        </Typography>
                                    </Grid>
                                    <DataGrid
                                        sx={{ height: '60vh' }}
                                        columns={columns}
                                        rows={members.NoCallOrNoShowAppointmentMembers}
                                        getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Grid size={12} textAlign="center" paddingBottom={0.5}>
                                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                            Completed ({members.CompletedAppointmentMembers.length})
                                        </Typography>
                                    </Grid>
                                    <DataGrid
                                        sx={{ height: '60vh' }}
                                        columns={columns}
                                        rows={members.CompletedAppointmentMembers}
                                        getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Grid size={12} textAlign="center" paddingBottom={0.5}>
                                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                            Unconfirmed ({members.UnconfirmedAppointmentMembers.length})
                                        </Typography>
                                    </Grid>
                                    <DataGrid
                                        sx={{ height: '60vh' }}
                                        columns={columns}
                                        rows={members.UnconfirmedAppointmentMembers}
                                        getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Grid size={12} textAlign="center" paddingBottom={0.5}>
                                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                            Rescheduled ({members.RescheduledAppointmentMembers.length})
                                        </Typography>
                                    </Grid>
                                    <DataGrid
                                        sx={{ height: '60vh' }}
                                        columns={columns}
                                        rows={members.RescheduledAppointmentMembers}
                                        getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/scheduling`, '_blank');
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default TeamAppointmentsHubDetail;
